<template>
  <wui-modal
    :title="$t('common.joinToChat')"
    :size="40"
    :closable="true"
  >
    <div class="flex items-center justify-center gap-4 p-4">
      <div class="shrink-0">
        <contact-avatar
          :image="roomInfo.avatar ? getFullURL(roomInfo.avatar) : null"
          :name="roomInfo.name"
          :is-room="true"
        />
      </div>
      <div class="flex flex-col gap-1 text-1624 font-light">
        <span class="font-medium text-fg-base-fg3">{{ roomInfo.name }}</span>
        <span class="font-light text-fg-base-fg22">{{ countMemberTextView }}</span>
      </div>
    </div>
    <div class="text-center text-1624 font-light py-6">
      <template v-if="isLoading">
        <dots-loader />
        <div>
          {{ $t('common.joinToChatAwaitDescription') }}
        </div>
      </template>
      <template v-else>
        <div>
          {{ $t('common.joinToChatConfirmed') }}
        </div>
      </template>
    </div>
  </wui-modal>
</template>

<script setup lang="ts">
import { WuiModal, closeDialogKey, safeInject } from '@wui/common-library'
import { BriefStatusResponse, GroupStatusEvent, useIM } from '@wui/im'
import { computed, ref } from 'vue'

import { ContactAvatar } from '@/entities/contact-avatar'

import { DotsLoader } from '@/shared/components/loaders'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { getTextForDifferentCount } from '@/shared/utils/get-text-for-different-count'
import { optionalPush } from '@/shared/utils/router'
import { fromPServedUser } from '@/shared/utils/uri'
import { getChatRoute, getFullURL } from '@/shared/utils/url'

const props = defineProps<{
  pServed: string,
  roomInfo: BriefStatusResponse
  skipChatNavigation?: boolean
}>()

const { tc } = useLocalization()
const { currentUser } = useCurrentUser()
const { onConferenceUpdated } = useIM()
const isLoading = ref(true)

onConferenceUpdated((event: GroupStatusEvent) => {
  const { extendedParams: { contacts }, pServed } = event
  if (!isLoading.value ||props.pServed !== pServed) {
    return
  }
  if (contacts?.find(({ id }) => id === currentUser.value?.imLogin)) {
    isLoading.value = false
    const navigate = props.skipChatNavigation
      ? Promise.resolve()
      : optionalPush(getChatRoute(fromPServedUser(pServed)))
    navigate.then(() => {
      closeModal(true)
    })
  }
})

const closeModal = safeInject(closeDialogKey)

const wordsForCountMemberText = {
  forOne: tc('common.participant', 0),
  forTwo: tc('common.participant', 1),
  forFive: tc('common.participant', 2),
}

const countMemberTextView = computed(() => getTextForDifferentCount(props.roomInfo.count, wordsForCountMemberText))
</script>
